import React, { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import Form from "antd/es/form";
import { useForm } from "antd/es/form/Form";
import { useIntl } from "react-intl";

import HasSourceObject from "@mapmycustomers/shared/types/base/HasSourceObject";
import { Company, EntityType, Person } from "@mapmycustomers/shared/types/entity";
import FormLayout, { LayoutSchemaField } from "@mapmycustomers/shared/types/layout/FormLayout";
import { isDefined } from "@mapmycustomers/shared/util/assert";
import { LoadingSpinner } from "@mapmycustomers/ui";

import Footer from "@app/component/createEditEntity/components/Footer";
import DuplicatesTable from "@app/component/DuplicatesTable";
import convertFieldToLayoutSchemaField from "@app/component/FormFields/utils/convertFieldToLayoutSchemaField";
import FormLayoutSelector from "@app/component/FormLayoutSelector";
import FormNoLayoutAlert from "@app/component/FormNoLayoutAlert";
import EntityModal from "@app/component/modal/EntityModal";
import { showGlobalPostCreationModal } from "@app/component/PostCreationModal/store/actions";
import PreviewModeIndicator from "@app/component/PreviewModeIndicator";
import { CreateCompanyPayload } from "@app/store/api/ApiService";
import { fetchCompany } from "@app/store/company/actions";
import { getCurrentUserId } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import AnalyticsContext from "@app/util/contexts/AnalyticsContext";
import { CompanyFieldName } from "@app/util/fieldModel/CompanyFieldModel";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";
import useFormChangeTracker from "@app/util/hook/form/useFormChangeTracker";
import useDefaultSourceObjectKey from "@app/util/hook/useDefaultSourceObjectKey";
import useErroredFieldsScroller from "@app/util/hook/useErroredFieldsScroller";
import useFieldValidation from "@app/util/hook/useFieldValidation";
import useSourceObjectMessageArgs from "@app/util/hook/useSourceObjectMessageArgs";
import { companyLayoutModel } from "@app/util/layout/impl";

import { companyAnalytics } from "../analytics";
import GeocodeLimitField from "../components/GeocodeLimitField";
import { getAddress } from "../util/getAddress";

import CompanyFormFields from "./CompanyFormFields";
import { getDuplicates, getUploadedFilesList, isCreating, isInitializing } from "./store";
import {
  clearAllUploadedCompanyFiles,
  clearDuplicates,
  createCompany,
  initialize,
} from "./store/actions";
import { isGlobalAddCompany } from "./store/selectors";
import FormValues from "./types/FormValues";
import getInitialValues from "./util/getInitialValues";

export interface CreateCompanyModalProps {
  className?: string;
  fixedParentCompanyId?: Company["id"];
  fixedPerson?: Person;
  initialValues?: Partial<Company>;
  leadGen?: boolean;
  okText?: ReactNode;
  onHide: (newCompany?: Company) => void;
  previewLayout?: FormLayout;
  previewSourceObject?: HasSourceObject["sourceObject"];
  previewWarning?: ReactNode;
  showPostCreationModal?: boolean;
}

const CreateCompanyModal = ({
  className,
  clearAllUploadedFiles,
  createCompany,
  creating,
  currentUserId,
  duplicates,
  fixedParentCompanyId,
  fixedPerson,
  initialize,
  initializing,
  initialValues,
  leadGen,
  okText,
  onClearDuplicates,
  onFetchCompany,
  onHide,
  onShowGlobalPostCreationModal,
  previewLayout,
  previewSourceObject,
  previewWarning,
  showPostCreationModal,
  uploadedFiles,
}: CreateCompanyModalProps & ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps) => {
  const intl = useIntl();

  const [formLayout, setFormLayout] = useState(companyLayoutModel.defaultFormLayout);

  const [form] = useForm<FormValues>();
  const [isFormChanged, handleFormChanges] = useFormChangeTracker(form);
  const [formContainer, handleFinishFail] = useErroredFieldsScroller();

  useEffect(() => {
    initialize();
  }, [form, initialize]);

  useEffect(() => {
    if (fixedParentCompanyId) {
      onFetchCompany({
        id: fixedParentCompanyId,
        callback: (company) => {
          form.setFieldsValue({ parentCompanyId: company.id, parentCompanyName: company.name });
        },
      });
    }
  }, [onFetchCompany, form, fixedParentCompanyId]);

  const handleCancelClick = useCallback(() => {
    clearAllUploadedFiles();
    onHide();
  }, [clearAllUploadedFiles, onHide]);

  const handleVisibility = useCallback(
    (company: Company) => {
      if (showPostCreationModal) {
        onShowGlobalPostCreationModal({ entity: company, entityType: EntityType.COMPANY });
      }
      onHide(company);
    },
    [showPostCreationModal, onHide, onShowGlobalPostCreationModal]
  );

  const handleFieldValidationErrors = useFieldValidation(form, handleFinishFail, true);

  const handleCreate = useCallback(
    (checkDuplicates: boolean) => {
      const layoutId = formLayout?.id;

      if (layoutId) {
        const values = form.getFieldsValue();
        const address = getAddress(values, initialValues?.geoManagementState);
        const company: CreateCompanyPayload = {
          ...address,
          annualRevenue: values.annualRevenue,
          cadenceInterval: values.cadenceInterval,
          color: values.color,
          crmActivityTypeId: values.crmActivityTypeId,
          email: values.email,
          geoAddress: initialValues?.geoAddress ?? address.geoAddress,
          geoPoint: initialValues?.geoPoint ?? address.geoPoint,
          name: values.name,
          numEmployees: values.numEmployees,
          parentAccount: values.parentCompanyId ? { id: values.parentCompanyId } : undefined,
          phone: values.phone,
          user: values.user ? { id: values.user } : undefined,
          website: values.website,
        };

        createCompany({
          callback: handleVisibility,
          checkDuplicates,
          company,
          customFieldsValues: values.customFields
            ? Object.values(values.customFields).filter(isDefined)
            : [],
          fieldsValueValidateCallback: handleFieldValidationErrors,
          groupsIdsToAdd: values.groups ? Array.from(values.groups) : [],
          layoutId,
          leadGen,
          personId: values.personId,
        });
      }
    },
    [
      createCompany,
      form,
      formLayout?.id,
      handleFieldValidationErrors,
      handleVisibility,
      initialValues?.geoAddress,
      initialValues?.geoManagementState,
      initialValues?.geoPoint,
      leadGen,
    ]
  );

  const defaultSourceObjectKey = useDefaultSourceObjectKey(EntityType.COMPANY);
  const isPreviewMode = !!previewLayout;
  // undefined means "not previewing", so the org default applies; null means "preview the
  // standard object". That's why not using ??.
  const sourceObject = isDefined(previewSourceObject)
    ? previewSourceObject
    : defaultSourceObjectKey;
  const titleMessageArgs = useSourceObjectMessageArgs(EntityType.COMPANY, sourceObject);
  const currentLayout = isPreviewMode ? previewLayout : formLayout;
  const submitButtonDisabled = creating || initializing || isPreviewMode || !currentLayout;
  const showConfirm =
    !isPreviewMode && !initializing && (isFormChanged || uploadedFiles.length > 0);

  const hasDuplicates = duplicates.length > 0;

  const handleCreateClick = useCallback(
    (checkForDuplicates: boolean) => {
      if (checkForDuplicates) {
        form.submit();
      } else {
        handleCreate(false);
      }
    },
    [form, handleCreate]
  );

  const handleFormFinish = useCallback(() => {
    handleCreate(true);
  }, [handleCreate]);

  const defaultAddedFields = useMemo(() => {
    const fieldModel = getFieldModelByEntityType(EntityType.COMPANY);
    const fieldNamesToAdd: CompanyFieldName[] = [];

    if (fixedParentCompanyId) {
      fieldNamesToAdd.push(CompanyFieldName.PARENT_COMPANY);
    }

    if (fixedPerson) {
      fieldNamesToAdd.push(CompanyFieldName.PERSON);
    }

    return fieldNamesToAdd.reduce((schemaField: LayoutSchemaField[], name) => {
      const field = fieldModel.getByName(name);
      if (field) {
        schemaField.push(convertFieldToLayoutSchemaField(field));
      }
      return schemaField;
    }, []);
  }, [fixedParentCompanyId, fixedPerson]);

  return (
    <AnalyticsContext.Provider value={companyAnalytics}>
      <EntityModal
        className={className}
        entityTypeName="Company"
        footer={
          <Footer
            creating={creating}
            entityType={EntityType.COMPANY}
            hasDuplicates={hasDuplicates}
            isPreviewMode={isPreviewMode}
            okDisabled={submitButtonDisabled}
            okText={okText}
            onCancel={handleCancelClick}
            onCreate={handleCreateClick}
            onGoBack={onClearDuplicates}
            sourceObjectArgs={titleMessageArgs}
          />
        }
        onCancel={handleCancelClick}
        showConfirm={showConfirm}
        title={
          hasDuplicates ? null : (
            <>
              {intl.formatMessage(
                {
                  id: "createCompanyModal.title",
                  defaultMessage:
                    "{sourceObject, select, standard {Create Company} leadCompanies {Create Lead} siteCompanies {Create Site} projectCompanies {Create Project} shopCompanies {Create Shop} other {Create {label}}}",
                  description:
                    'Title of Create Company modal, named after the source object. The record type name comes from the select: the web\'s own word for a known type, otherwise {label}, a name an admin typed or an integration sent. Keep {label} in its base form. In es, pt and fr, no article, adjective or participle may agree with it: put gendered words on the noun "record" with the name in parentheses, or put a verb before the name. In a choice that creates a record, use a form like "Добавить: {label}".',
                },
                titleMessageArgs
              )}

              {isPreviewMode ? (
                <PreviewModeIndicator />
              ) : (
                <FormLayoutSelector
                  disabled={creating || initializing}
                  entityType={EntityType.COMPANY}
                  layout={formLayout}
                  onChange={setFormLayout}
                />
              )}
            </>
          )
        }
      >
        {initializing ? (
          <LoadingSpinner />
        ) : currentLayout ? (
          <>
            {hasDuplicates ? (
              <DuplicatesTable
                duplicates={duplicates}
                entityType={EntityType.COMPANY}
                showAsLinks
              />
            ) : null}

            <div hidden={hasDuplicates} ref={formContainer}>
              <GeocodeLimitField />
              <Form
                form={form}
                initialValues={getInitialValues(
                  currentUserId,
                  sourceObject,
                  initialValues,
                  fixedPerson
                )}
                layout="vertical"
                onFinish={handleFormFinish}
                onFinishFailed={handleFinishFail}
                onValuesChange={handleFormChanges}
                preserve
              >
                <CompanyFormFields
                  defaultAddedFields={defaultAddedFields}
                  fixedPerson={fixedPerson}
                  layout={currentLayout}
                  previewWarning={previewWarning}
                />
              </Form>
            </div>
          </>
        ) : (
          <FormNoLayoutAlert />
        )}
      </EntityModal>
    </AnalyticsContext.Provider>
  );
};

const mapStateToProps = (state: RootState, { showPostCreationModal }: CreateCompanyModalProps) => ({
  creating: isCreating(state),
  currentUserId: getCurrentUserId(state)!,
  duplicates: getDuplicates(state),
  initializing: isInitializing(state),
  showPostCreationModal: showPostCreationModal ?? isGlobalAddCompany(state),
  uploadedFiles: getUploadedFilesList(state),
});

const mapDispatchToProps = {
  clearAllUploadedFiles: clearAllUploadedCompanyFiles,
  createCompany: createCompany.request,
  initialize: initialize.request,
  onClearDuplicates: clearDuplicates,
  onFetchCompany: fetchCompany,
  onShowGlobalPostCreationModal: showGlobalPostCreationModal,
};

export default connect(mapStateToProps, mapDispatchToProps)(CreateCompanyModal);
